import React from 'react';

import { FormProvider } from 'react-hook-form';

import { ConfirmChangesModal } from 'components/common/confirmChangesModal/confirmChangesModal';

import { IAssignee } from 'components/Dashboards/Program/Dashboard/ProgramDashboardComponents/Battlecards/battlecards.types';
import { CustomizedButton } from 'components/common';
import { AuditLog } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/AuditLog/AuditLog';
import { CardHeader } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/CardHeader/CardHeader';
import { Details } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/Details/Details';
import { LinkedActivities } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/LinkedActivities/LinkedActivities';
import { Tabs } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/Tabs/Tabs';
import { useUpdateRiskRegisterFormLogic } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/UpdateRiskRegisterModal/components/UpdateRiskRegisterForm/UpdateRiskRegisterForm.logic';
import classes from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/UpdateRiskRegisterModal/components/UpdateRiskRegisterForm/UpdateRiskRegisterForm.module.scss';
import { useUpdateRiskRegisterFormProvider } from 'components/widgets/RiskRegister/components/RiskRegisterRoutes/components/UpdateRiskRegisterModal/components/UpdateRiskRegisterForm/UpdateRiskRegisterForm.provider';

interface Props {
    handleModalClose: () => void;
}

export interface FormProps {
    id: number;
    cardId: number;
    name: string;
    status: 'NEW' | 'OPEN' | 'CLOSED';
    probability: 'VERY_LOW' | 'LOW' | 'MEDIUM' | 'HIGH' | 'VERY_HIGH' | 'CERTAIN';
    impactType: 'RELATIVE' | 'ABSOLUTE';
    distribution: 'UNIFORM' | 'TRIANGLE' | 'BETA_PERT';
    distributionRange: {
        min: number;
        middle: number | null;
        max: number;
    };
    assignee: string;
    assigneeValue: ISelectOption<string> & { assignee: IAssignee };
    linkedActivities: number;
    description: string;
    contractId: number;
    linkOption: 'wbs' | 'activityCodes' | 'cluster' | 'specific' | 'segment' | 'all';
    activitiesByKey: {
        wbs?: number | null;
        activityCodes?: number[] | null;
        cluster?: number | null;
        specific?: string[] | null;
        segment?: number | null;
        all?: null;
    };
    tags: { name: string }[];
}

export const UpdateRiskRegisterForm = ({ handleModalClose }: Props) => {
    const { api, ui } = useUpdateRiskRegisterFormProvider();
    const { logic } = useUpdateRiskRegisterFormLogic({ api, ui, handleModalClose });

    return (
        <FormProvider {...logic.form}>
            <form onSubmit={logic.form.handleSubmit(logic.handleFormSubmit)} className={classes.form}>
                <CardHeader title={'Risk Item'} />
                <Tabs activeTab={ui.state.activeTab} setActiveTab={ui.setters.setActiveTab} />
                {ui.state.activeTab === 'details' && <Details />}
                {ui.state.activeTab === 'linkedActivities' && <LinkedActivities />}
                {ui.state.activeTab === 'auditLog' && <AuditLog />}

                <div className={classes.formActions}>
                    <CustomizedButton size="large" color="secondary" onClick={logic.closeModalConfirm}>
                        Close
                    </CustomizedButton>
                    <CustomizedButton
                        size="large"
                        type="submit"
                        color="primary"
                        disabled={!logic.form.formState.isDirty}
                    >
                        Save
                    </CustomizedButton>
                </div>
            </form>
            <ConfirmChangesModal
                showModal={ui.state.showConfirmModal}
                onHideModal={() => ui.setters.setShowConfirmModal(false)}
                handleCloseWithoutSave={handleModalClose}
                handleSave={logic.form.handleSubmit(logic.handleFormSubmit)}
                isLoading={api.updateRisk.instance.isLoading}
            >
                You made changes to this card. Would you like to save it?
            </ConfirmChangesModal>
        </FormProvider>
    );
};
